import React, { useEffect, useState, useMemo } from "react";
import { adminGet, adminPost, adminDelete } from "../../Services";
import { AgGridReact } from "ag-grid-react";
import {
  ClientSideRowModelModule,
  GridStateModule,
  RowSelectionModule,
  ModuleRegistry,
} from "ag-grid-community";
import {
  Container,
  Spinner,
  Form,
  Modal,
  Row,
  Col,
  Card,
  Button,
  Badge,
} from "react-bootstrap";
import { toast } from "react-toastify";
import { formatDateTime } from "../../utils/helpers";

ModuleRegistry.registerModules([
  RowSelectionModule,
  GridStateModule,
  ClientSideRowModelModule,
]);

const BlacklistedMembers = () => {
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gridApi, setGridApi] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const fetchBlacklistedMembers = () => {
    setLoading(true);
    adminGet("/api/blacklist")
      .then((res) => {
        setRowData(res.data);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchBlacklistedMembers();
  }, []);

  const columnDefs = [
    { headerName: "Name", field: "name", sortable: true },
    { headerName: "Place", field: "place", sortable: true },
    { headerName: "Reason", field: "reason", sortable: true },
    {
      headerName: "Actions",
      field: "actions",
      cellRenderer: (params) => (
        <div className="d-flex justify-content-center">
          <Button variant="primary" size="sm" onClick={() => {
            setSelectedData(params.data);
            setShowModal(true);
          }}>
            Edit
          </Button>
          <Button variant="danger" size="sm" className="ms-2" onClick={() => {
            setSelectedData(params.data);
            setShowDeleteModal(true);
          }}>
            Delete
          </Button>
        </div>
      ),
      pinned: "right",
      width: 150,
    },
  ];

  const onGridReady = (params) => {
    setGridApi(params.api);
  };

  const handleDelete = () => {
    setLoading(true);
    adminDelete(`/api/blacklist/${selectedData._id}`)
      .then((res) => {
        toast.success("Member deleted successfully!");
        fetchBlacklistedMembers();
        setShowDeleteModal(false);
      })
      .catch((err) => {
        toast.error(err.response.data.error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div className="p-2 pt-0">
      <div className="mb-2 d-flex align-items-center justify-content-between">
        <h3 className="fw-medium" style={{ color: "#1a5276" }}>
          Blacklisted Members
        </h3>
        <Button variant="primary" onClick={() => {
          setSelectedData({});
          setShowModal(true);
        }}>
          Create
        </Button>
      </div>

      <div className="position-relative">
        {loading && (
          <div className="d-flex justify-content-center align-items-center position-absolute top-0 start-0 w-100 h-100 bg-white bg-opacity-75">
            <Spinner animation="border" variant="primary" />
          </div>
        )}

        <div
          className="ag-theme-alpine"
          style={{ height: "80vh", width: "100%" }}
        >
          <AgGridReact
            columnDefs={columnDefs}
            rowData={rowData}
            onGridReady={onGridReady}
            overlayLoadingTemplate='<span class="ag-overlay-loading-center">Loading...</span>'
            overlayNoRowsTemplate='<span class="ag-overlay-no-rows-center">No records found.</span>'
          />
        </div>
      </div>

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{selectedData._id ? "Edit" : "Create"} Blacklisted Member</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={selectedData.name || ""}
                onChange={(e) => setSelectedData({ ...selectedData, name: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Place</Form.Label>
              <Form.Control
                type="text"
                value={selectedData.place || ""}
                onChange={(e) => setSelectedData({ ...selectedData, place: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Reason</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={selectedData.reason || ""}
                onChange={(e) => setSelectedData({ ...selectedData, reason: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Image</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={(e) => setSelectedData({ ...selectedData, image: e.target.files[0] })}
              />
              {selectedData.image && (
                <img
                  src={typeof selectedData.image === 'string' ? selectedData.image : URL.createObjectURL(selectedData.image)}
                  alt="Preview"
                  className="mt-2"
                  style={{ width: "100px", height: "100px", objectFit: "cover" }}
                />
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={() => {
            setLoading(true);
            const formData = new FormData();
            formData.append("name", selectedData.name);
            formData.append("place", selectedData.place);
            formData.append("reason", selectedData.reason);
            if (selectedData.image) {
              formData.append("image", selectedData.image);
            }

            const promise = selectedData._id
              ? adminPost(`/api/blacklist/${selectedData._id}`, formData)
              : adminPost("/api/blacklist", formData);

            promise
              .then((res) => {
                toast.success(`Member ${selectedData._id ? "updated" : "created"} successfully!`);
                fetchBlacklistedMembers();
                setShowModal(false);
              })
              .catch((err) => {
                toast.error(err.response.data.error);
              })
              .finally(() => {
                setLoading(false);
              });
          }}>
            {selectedData._id ? "Save Changes" : "Create"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this member?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default BlacklistedMembers;
